/**
 * @file Renders a grid of popular category cards for the search UI.
 */
import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Grid } from '@stvgroup/stv-ui-components'
import playerApi from '../../utils/playerApi'
import SearchCard from './SearchCard'
import mostPopularImage from '../../assets/images/most-popular-category.png'

/**
 * @typedef {object} LocalCategoryData
 * @property {string} [title] - An optional override for the category title.
 * @property {string} [url] - An optional override for the navigation URL.
 * @property {string} [background] - An optional override for the card's background style.
 */

/**
 * Local configuration for popular categories.
 * This object defines a static list of category GUIDs to display.
 * It allows for overriding properties like title, URL, or background for specific categories,
 * providing a way to customize their appearance and behavior independently of the CMS data.
 *
 * The keys are category GUIDs.
 * @type {Object.<string, LocalCategoryData>}
 */
const popularCategoriesData = {
  entertainment: {},
  dramas: {},
  'most-popular': {
    title: 'Most Popular',
    url: '/categories/most-popular',
    image: mostPopularImage,
  },
  'crime-drama': {
    title: 'Crime',
  },
  'the-sport-hub': {
    title: 'Sport',
  },
  live: {
    title: '• Live',
    url: '/live',
    background: 'linear-gradient(180deg, #FF0000 0%, #A00000 100%)',
  },
  documentaries: {
    title: 'Documentaries & Lifestyle',
  },
  soaps: {},
}

/**
 * A component that fetches, processes, and displays a grid of popular categories.
 * It merges category data from the Player API with local overrides defined in
 * `popularCategoriesData`.
 * @returns {React.Component} A grid of `SearchCard` components.
 */
const PopularCategories = () => {
  const [categories, setCategories] = useState([])
  const dispatch = useDispatch()
  // Attempt to retrieve category data from the Redux cache to avoid redundant API calls.
  const cachedCategories = useSelector(state => state.playerApiCache['/categories'])

  useEffect(() => {
    /**
     * Processes the raw category list from the API, merges it with local overrides,
     * and updates the component's state.
     * @param {Array<object>} allCategories - The array of category objects from the API.
     */
    const processCategories = (allCategories) => {
      if (!allCategories) return

      // Create a lookup map for quick access to categories by their GUID.
      const categoriesByGuid = allCategories.reduce((acc, category) => ({
        ...acc,
        [category.guid]: category,
      }), {})

      // Map over the statically defined popular categories and merge with API data.
      const popularCategories = Object.keys(popularCategoriesData).map((guid) => {
        const cmsCategory = categoriesByGuid[guid] || {}
        const localCategory = popularCategoriesData[guid]

        const promotedImage = cmsCategory.promotedProgramme?.images?.find(img => img.imageType === 'mainImage')

        // Merge properties, with local data taking precedence.
        return {
          ...cmsCategory,
          ...localCategory,
          guid,
          name: localCategory.title || cmsCategory.name, // Prioritize local title override.
          image: localCategory.image || (promotedImage ? `https://player.api.stvqa.tv/v1/images/${promotedImage.id}/rendition/?redirect=true&width=512&height=288` : cmsCategory?.images?._filepath),
        }
      })

      setCategories(popularCategories)
    }

    /**
     * Fetches the full list of categories from the API.
     */
    const fetchCategories = async () => {
      const { results, reason } = await playerApi()({ dispatch, path: '/categories' })
      if (reason) {
        // It's better to handle errors gracefully, but for now, we throw.
        throw new Error(`Failed to fetch categories: ${reason.message}`)
      }
      processCategories(results)
    }

    // Use cached data if available, otherwise fetch from the API.
    if (cachedCategories) {
      processCategories(cachedCategories.results)
    } else {
      fetchCategories()
    }
  }, [cachedCategories, dispatch])

  return (
    <Grid container spacing={{ xs: 1, md: 2 }}>
      {categories.map(({
        guid, image, background, title, name, url,
      }) => {
        // Determine the image source and loading state for the card.
        const loading = !image && !background

        return (
          <Grid item key={guid} xs={6} sm={4} md={3}>
            <SearchCard
              title={title || name}
              image={image}
              url={url || `/categories/${guid}`}
              background={background}
              loading={loading}
            />
          </Grid>
        )
      })}
    </Grid>
  )
}

export default PopularCategories
